import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../utils/api';
import Avatar from './Avatar';

// Fix 4: removed DEPARTMENTS list - department is optional/minimal
export default function ProfileModal({ onClose, initialTab = 'profile' }) {
  const { user, updateUser } = useAuth();
  const [tab, setTab] = useState(initialTab); // Fix 1: opens directly on correct tab
  const [form, setForm] = useState({
    name: user?.name || '',
    designation: user?.designation || '',
    phone: user?.phone || '',
    bio: user?.bio || ''
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = useRef(null);

  const handleProfileSave = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await usersAPI.updateProfile(form);
      updateUser(res.data.data);
      toast.success('Profile updated!');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile.');
    } finally { setLoading(false); }
  };

  const validatePw = () => {
    const errs = {};
    if (!pwForm.currentPassword) errs.currentPassword = 'Current password required';
    if (!pwForm.newPassword || pwForm.newPassword.length < 8) errs.newPassword = 'Minimum 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(pwForm.newPassword)) errs.newPassword = 'Uppercase, lowercase and number required';
    if (pwForm.newPassword !== pwForm.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handlePasswordChange = async e => {
    e.preventDefault();
    const errs = validatePw();
    if (Object.keys(errs).length) { setPwErrors(errs); return; }
    setLoading(true);
    try {
      await usersAPI.changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      toast.success('✅ Password changed successfully!');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwErrors({});
      setTimeout(onClose, 1000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password.');
    } finally { setLoading(false); }
  };

  const handleAvatarUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('avatar', file);
    setUploadingAvatar(true);
    try {
      const res = await usersAPI.uploadAvatar(fd);
      updateUser({ avatar: res.data.data.avatar });
      toast.success('Photo updated!');
    } catch { toast.error('Upload failed. Max 5MB, images only.'); }
    finally { setUploadingAvatar(false); }
  };

  // Password strength
  const getStrength = pwd => {
    if (!pwd) return 0;
    let s = 0;
    if (pwd.length >= 8) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };
  const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981'];
  const pwStrength = getStrength(pwForm.newPassword);

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">
            {tab === 'password' ? '🔒 Change Password' : '👤 My Profile'}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Avatar section — only show on profile tab */}
        {tab === 'profile' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
            <label className="avatar-upload-wrap" onClick={() => fileRef.current?.click()}>
              <Avatar user={user} size="xl" />
              <div className="avatar-upload-overlay">{uploadingAvatar ? '⏳' : '📷'}</div>
              <input type="file" ref={fileRef} hidden accept="image/*" onChange={handleAvatarUpload} />
            </label>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{user?.name}</div>
              <div style={{ color: 'var(--text-3)', fontSize: 12 }}>{user?.email}</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>
              {uploadingAvatar ? 'Uploading…' : '📷 Change Photo'}
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="modal-tabs" style={{ margin: '0 24px' }}>
          <button className={`modal-tab${tab === 'profile' ? ' active' : ''}`} onClick={() => setTab('profile')}>
            👤 Profile
          </button>
          <button className={`modal-tab${tab === 'password' ? ' active' : ''}`} onClick={() => setTab('password')}>
            🔒 Change Password
          </button>
        </div>

        <div className="modal-body" style={{ paddingTop: 4 }}>
          {tab === 'profile' ? (
            <form onSubmit={handleProfileSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Designation</label>
                  <input className="form-input" value={form.designation}
                    onChange={e => setForm(f => ({ ...f, designation: e.target.value }))}
                    placeholder="e.g. Senior Manager" />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input className="form-input" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91 98765 43210" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bio</label>
                <textarea className="form-input" value={form.bio} rows={3}
                  style={{ height: 'auto', padding: '10px 12px', resize: 'vertical' }}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="Tell your colleagues about yourself…" maxLength={300} />
              </div>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Saving…' : 'Save Changes'}
              </button>
            </form>
          ) : (
            /* Fix 1: Change Password panel - now directly accessible */
            <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="alert alert-info" style={{ fontSize: 12 }}>
                Your new password must be at least 8 characters with uppercase, lowercase, and a number.
              </div>

              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input type="password"
                  className={`form-input${pwErrors.currentPassword ? ' error' : ''}`}
                  value={pwForm.currentPassword}
                  onChange={e => { setPwForm(f => ({ ...f, currentPassword: e.target.value })); setPwErrors(er => ({ ...er, currentPassword: '' })); }}
                  placeholder="Enter your current password"
                  autoComplete="current-password" />
                {pwErrors.currentPassword && <span className="form-error">{pwErrors.currentPassword}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password"
                  className={`form-input${pwErrors.newPassword ? ' error' : ''}`}
                  value={pwForm.newPassword}
                  onChange={e => { setPwForm(f => ({ ...f, newPassword: e.target.value })); setPwErrors(er => ({ ...er, newPassword: '' })); }}
                  placeholder="Enter new password"
                  autoComplete="new-password" />
                {pwErrors.newPassword && <span className="form-error">{pwErrors.newPassword}</span>}
                {/* Strength meter */}
                {pwForm.newPassword && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 3, marginBottom: 3 }}>
                      {[1,2,3,4].map(i => (
                        <div key={i} style={{ flex: 1, height: 3, borderRadius: 3, background: i <= pwStrength ? strengthColors[pwStrength] : 'var(--border)', transition: 'background 0.2s' }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 11, color: strengthColors[pwStrength], fontWeight: 600 }}>
                      {strengthLabels[pwStrength]}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input type="password"
                  className={`form-input${pwErrors.confirmPassword ? ' error' : ''}`}
                  value={pwForm.confirmPassword}
                  onChange={e => { setPwForm(f => ({ ...f, confirmPassword: e.target.value })); setPwErrors(er => ({ ...er, confirmPassword: '' })); }}
                  placeholder="Repeat new password"
                  autoComplete="new-password" />
                {pwErrors.confirmPassword && <span className="form-error">{pwErrors.confirmPassword}</span>}
                {pwForm.confirmPassword && pwForm.newPassword === pwForm.confirmPassword && (
                  <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 3, fontWeight: 600 }}>✓ Passwords match</div>
                )}
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Changing Password…' : '🔒 Change Password'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
